import { useState, type FormEvent } from 'react'
import { del, get, post } from '../../../api'
import { useAuth } from '../../../auth'
import { Alert, Button, Card, Field, Input, Select, Spinner } from '../../../components/ui'
import { useAction, useLoad } from '../../../hooks'
import type { Invite, Member, Role } from '../../../types'
import { useBusiness } from '../Layout'

const ROLE_HELP: Record<Role, string> = {
  staff: 'Runs the day: sees and takes bookings, seats guests.',
  manager: 'Staff plus hours, areas and closures.',
  owner: 'Everything, including settings, team and the website key.',
}

export function TeamSection() {
  const { business } = useBusiness()
  const { me } = useAuth()
  const team = useLoad(() => get<{ members: Member[]; invites: Invite[] }>(`/v1/admin/businesses/${business.id}/members`), [business.id])
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('staff')
  const [inviteUrl, setInviteUrl] = useState<string | null>(null)
  const invite = useAction(async () => {
    const r = await post<{ inviteUrl: string }>(`/v1/admin/businesses/${business.id}/invites`, { email: email.trim(), role })
    setInviteUrl(r.inviteUrl)
    setEmail('')
    await team.reload({ quiet: true })
  })
  const remove = useAction(async (m: Member) => {
    if (!confirm(`Remove ${m.name ?? m.email} from ${business.name}?`)) return
    await del(`/v1/admin/businesses/${business.id}/members/${m.userId}`)
    await team.reload({ quiet: true })
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    void invite.run()
  }
  return (
    <div className="stack-lg">
      <Card title="Invite someone">
        <form onSubmit={submit} className="stack">
          <div className="row-flex" style={{ alignItems: 'flex-end' }}>
            <Field label="Email" className="grow">{(id) => <Input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />}</Field>
            <Field label="Role">
              {(id) => (
                <Select id={id} value={role} onChange={(e) => setRole(e.target.value as Role)}>
                  <option value="staff">Staff</option>
                  <option value="manager">Manager</option>
                  <option value="owner">Owner</option>
                </Select>
              )}
            </Field>
            <Button type="submit" variant="primary" busy={invite.busy}>
              Create invitation
            </Button>
          </div>
          <p className="hint">{ROLE_HELP[role]}</p>
          {invite.error ? <Alert>{invite.error}</Alert> : null}
          {inviteUrl ? (
            <Alert kind="success">
              <div className="stack">
                <span>Send this link to them. It works for 7 days, and only for an account with that email. If they already have an account, the invitation is applied the next time they sign in.</span>
                <code className="mono" style={{ wordBreak: 'break-all' }}>
                  {inviteUrl}
                </code>
                <div>
                  <Button size="sm" onClick={() => void navigator.clipboard.writeText(inviteUrl).catch(() => {})}>
                    Copy link
                  </Button>
                </div>
              </div>
            </Alert>
          ) : null}
        </form>
      </Card>
      <Card title="Team">
        {team.loading && !team.data ? <Spinner /> : null}
        {team.error ? <Alert>{team.error}</Alert> : null}
        {remove.error ? <Alert>{remove.error}</Alert> : null}
        <ul className="list-plain">
          {team.data?.members.map((m) => (
            <li key={m.userId}>
              <div>
                <div style={{ fontWeight: 600 }}>
                  {m.name ?? m.email} {m.userId === me?.user.id ? <span className="muted small">(you)</span> : null}
                </div>
                <div className="muted small">
                  {m.email} · {m.role}
                </div>
              </div>
              {m.userId !== me?.user.id ? (
                <Button size="sm" variant="ghost" onClick={() => void remove.run(m)}>
                  Remove
                </Button>
              ) : null}
            </li>
          ))}
          {team.data?.invites.map((i) => (
            <li key={i.id}>
              <div>
                <div style={{ fontWeight: 600 }}>{i.email}</div>
                <div className="muted small">
                  Invited as {i.role} · expires {new Date(i.expiresAt).toLocaleDateString()}
                </div>
              </div>
              <span className="chip">Pending</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  )
}
